/**
 * role="radiogroup" の矢印キー操作と、共通の radio pill ボタン。
 * React 合成イベントでは queueMicrotask 後に currentTarget が null になるため、
 * フォーカス移動用に group 要素を同期的に保持する。
 */

import type { KeyboardEvent, ReactNode } from 'react';

export type KeisukeRadioOption = {
  active: boolean;
  onSelect: () => void;
  children: ReactNode;
  disabled?: boolean;
  title?: string;
  key?: string;
};

export function handleRadioGroupArrowKey(
  e: KeyboardEvent<HTMLElement>,
  options: ReadonlyArray<Pick<KeisukeRadioOption, 'active' | 'onSelect' | 'disabled'>>
): void {
  if (
    e.key !== 'ArrowLeft' &&
    e.key !== 'ArrowRight' &&
    e.key !== 'ArrowUp' &&
    e.key !== 'ArrowDown'
  ) {
    return;
  }
  const enabled = options
    .map((opt, index) => ({ opt, index }))
    .filter(({ opt }) => !opt.disabled);
  if (enabled.length === 0) return;

  e.preventDefault();
  const group = e.currentTarget;
  const currentPos = enabled.findIndex(({ opt }) => opt.active);
  const from = currentPos >= 0 ? currentPos : 0;
  const delta = e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1;
  const next = enabled[(from + delta + enabled.length) % enabled.length];
  next.opt.onSelect();
  queueMicrotask(() => {
    group.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]')?.focus();
  });
}

/** @deprecated 互換: 左/上→第一、右/下→第二（旧 2 択の単純マッピング） */
export function handleBinaryRadioGroupArrowKey(
  e: KeyboardEvent<HTMLElement>,
  selectFirst: () => void,
  selectSecond: () => void
): void {
  if (
    e.key !== 'ArrowLeft' &&
    e.key !== 'ArrowRight' &&
    e.key !== 'ArrowUp' &&
    e.key !== 'ArrowDown'
  ) {
    return;
  }
  e.preventDefault();
  const group = e.currentTarget;
  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') selectFirst();
  else selectSecond();
  queueMicrotask(() => {
    group.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]')?.focus();
  });
}

/** LeftPane / 中央 t検定ピルで共有（見た目は className で差し替え） */
export function KeisukeBinaryRadioPill({
  active,
  onClick,
  children,
  className,
  activeClassName,
  disabled,
  title,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  className: string;
  activeClassName: string;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      tabIndex={active ? 0 : -1}
      className={`${className}${active ? ` ${activeClassName}` : ''}`}
      disabled={disabled}
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

type BinaryRadioOption = Omit<KeisukeRadioOption, 'key'>;

/**
 * 可視ラベル付き N 択 radiogroup（2択含む）。
 * ピル見た目は group 側で一括指定。
 */
export function KeisukeLabeledRadioGroup({
  label,
  className,
  labelClassName,
  groupClassName,
  pillClassName,
  pillActiveClassName,
  options,
  hideLabel,
}: {
  label: string;
  className?: string;
  labelClassName?: string;
  groupClassName: string;
  pillClassName: string;
  pillActiveClassName: string;
  options: ReadonlyArray<KeisukeRadioOption>;
  /** ラベルを視覚的に出さず aria-label のみ使う */
  hideLabel?: boolean;
}) {
  return (
    <div className={className}>
      {!hideLabel && labelClassName ? (
        <span className={labelClassName} aria-hidden>
          {label}
        </span>
      ) : null}
      <div
        className={groupClassName}
        role="radiogroup"
        aria-label={label}
        onKeyDown={(e) => handleRadioGroupArrowKey(e, options)}
      >
        {options.map((opt, index) => (
          <KeisukeBinaryRadioPill
            key={opt.key ?? String(index)}
            active={opt.active}
            disabled={opt.disabled}
            className={pillClassName}
            activeClassName={pillActiveClassName}
            title={opt.title}
            onClick={opt.onSelect}
          >
            {opt.children}
          </KeisukeBinaryRadioPill>
        ))}
      </div>
    </div>
  );
}

/**
 * 可視ラベル付き 2択 radiogroup（互換 API）。
 */
export function KeisukeLabeledBinaryRadioGroup({
  label,
  className,
  labelClassName,
  groupClassName,
  pillClassName,
  pillActiveClassName,
  first,
  second,
}: {
  label: string;
  className?: string;
  labelClassName: string;
  groupClassName: string;
  pillClassName: string;
  pillActiveClassName: string;
  first: BinaryRadioOption;
  second: BinaryRadioOption;
}) {
  return (
    <KeisukeLabeledRadioGroup
      label={label}
      className={className}
      labelClassName={labelClassName}
      groupClassName={groupClassName}
      pillClassName={pillClassName}
      pillActiveClassName={pillActiveClassName}
      options={[
        { ...first, key: 'first' },
        { ...second, key: 'second' },
      ]}
    />
  );
}
